import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { CloudDownload as CloudDownloadIcon } from '@mui/icons-material';
import type { ValidationError } from '../../types/nodeImport.types';

type ImportedNodeData = {
  label: string;
  nodeType: string; // original type string stored in workflow (e.g. n8n-nodes-base.httpRequest)
  description?: string;
  parameters?: any;
  isImported?: boolean;
  validationErrors?: ValidationError[];
};

export const ImportedNodeComponent: React.FC<NodeProps<ImportedNodeData>> = ({ data, selected }) => {
  const errors = data.validationErrors || [];
  const errorCount = errors.filter((e) => e.severity === 'error').length;
  const warningCount = errors.filter((e) => e.severity === 'warning').length;
  const hasIssues = errorCount > 0 || warningCount > 0;

  // Match the "ODA node" style: colored header + subtle border, with stronger emphasis on selection/issues.
  const borderColor = errorCount > 0 ? '#d32f2f' : warningCount > 0 ? '#ed6c02' : selected ? '#4ecdc4' : 'rgba(0,0,0,0.08)';
  const headerColor = errorCount > 0 ? '#d32f2f' : warningCount > 0 ? '#ed6c02' : '#45b7d1';

  return (
    <Paper
      elevation={selected ? 8 : 2}
      sx={{
        minWidth: 200,
        maxWidth: 320,
        maxHeight: 260,
        border: `2px solid ${borderColor}`,
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ bgcolor: headerColor, color: 'white', px: 2, py: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
          <Box display="flex" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
            <CloudDownloadIcon fontSize="small" />
            <Typography variant="subtitle2" fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
              {data.label}
            </Typography>
          </Box>
          {hasIssues && (
            <Chip
              size="small"
              label={errorCount > 0 ? `${errorCount} error` : `${warningCount} warning`}
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
            />
          )}
        </Box>
      </Box>

      <Box
        className="nowheel nodrag"
        onWheelCapture={(e) => e.stopPropagation()}
        sx={{
          px: 2,
          py: 1,
          maxHeight: 140,
          overflowY: 'auto',
          overflowX: 'hidden',
          pointerEvents: 'auto',
        }}
      >
        <Typography variant="caption" color="text.secondary" noWrap title={data.nodeType}>
          {data.nodeType}
        </Typography>
        {data.description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {data.description}
          </Typography>
        )}
        {errorCount > 0 && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: '#d32f2f' }}>
            Needs attention: check Node Settings for details.
          </Typography>
        )}
      </Box>

      <Handle type="target" position={Position.Top} style={{ background: '#555' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#555' }} />
    </Paper>
  );
};


